import usePermissions from "./PermissionsProvider";
import { Role } from "./PermissionsTypes";

/**
 * "May this user write to THIS project?", answered from the project's own membership role — plus the
 * wording every surface uses to say no.
 *
 * Why not `useProjectEditReadOnly`: that composable resolves the project from `useRoute().params.id`
 * and reads the single-project store, and both of those only exist under `/project/:id`. The projects
 * overview shows N projects at once, each with its own membership role, so the guard there has to be
 * per-row — and the role travels on every row already, as Hasura's `project_membership_role`
 * computed field on the `projectBase` fragment.
 *
 * The wording lives here rather than beside that composable so a Viewer is told the same thing
 * wherever they meet the wall (RTLCA-5583); the composable re-exports both constants, so its own
 * call sites are unchanged.
 */

/** One line, so it fits the `label` tooltip variant — same slot the revision preview hint occupies. */
export const VIEWER_READ_ONLY_HINT = "Read-only — you have Viewer access on this project";

/** Said out loud when a write is actually attempted by someone without `project.edit`. */
export const VIEWER_READ_ONLY_MESSAGE =
  "You have Viewer access on this project, so it is read-only. Ask a project admin for Contributor access to make changes.";

/**
 * What Hasura actually sends is `project.admin` / `project.contributor` / `project.viewer`, the
 * literal `accessAllProjects` for a tenant-wide user holding no membership row on the project, or
 * null when the project is invisible to them. `Role` names only the three unprefixed roles, so the
 * cast states what the permissions store already handles: `mapRole` strips the `project.` prefix,
 * and any value it does not recognise — `accessAllProjects` included — simply fails every
 * membership test below.
 *
 * A missing role fails closed. A guard that guesses "allowed" while the role is unknown is a guard
 * that lets the first click through.
 */
const toRoles = (role: string | null | undefined): Role[] => (role ? [role as Role] : []);

/** Whether this membership role may change the project's data (Contributor or Admin). */
export const canEditProjectRole = (role: string | null | undefined): boolean => usePermissions().canEdit(toRoles(role));

/** Whether this membership role may invite and manage the project's members. */
export const canManageProjectRole = (role: string | null | undefined): boolean => usePermissions().canManage(toRoles(role));
